/**
 * Contrato da seleção múltipla em `/admin/cartoes` e da barra de lote.
 *
 * A rota é `POST /review/cards/bulk` (`ReviewService.bulk`). O corpo já está
 * em `BulkCardsRequest`: `moveSubject` (o "mover para outro baralho" da tela —
 * o destino é uma matéria), `addTag`, `duplicate` e `delete`. A resposta traz
 * `affected` como contagem e `unaffected` como lista `{cardId, reason}`.
 * Quem mostra o resultado lê `BulkActionResult`; não compara com o tamanho
 * da seleção.
 *
 * Enquanto a rota responder 404 ou 405, a tela diz que a ação está
 * indisponível e não dispara uma requisição por cartão.
 */
import type { BulkActionResult } from '../../../sdk/@types/Revisao';

/** Destino de `moveSubject`. `id` é o `subjectId` da rota; `name` é o rótulo. */
export interface LoteBaralho {
  id: string;
  name: string;
}

/** O que a tela diz depois de um lote que a rota respondeu. */
export interface LoteRelato {
  /** Verdadeiro quando `notAffected` não está vazio. A frase não é sucesso total. */
  parcial: boolean;
  applied: number;
  skipped: number;
  message: string;
  /** Uma linha por item de `notAffected`, na ordem da rota. */
  motivos: string[];
}

export interface LoteBarProps {
  /** Cartões selecionados. `0` não desenha a barra. */
  count: number;
  baralhos: LoteBaralho[];
  busy: boolean;
  /** A rota de lote não existe. A barra não oferece as ações. */
  indisponivel: boolean;
  onMove: (subjectId: string) => void;
  onAddTag: (tag: string) => void;
  onDuplicate: () => void;
  /** Só depois que a pessoa confirmou, já tendo lido o alcance da exclusão. */
  onConfirmDelete: () => void;
}

/** Caixas da tabela. A view é dona do conjunto; a tabela só liga e desliga. */
export interface SelecaoLinhas {
  selectedIds: ReadonlySet<string>;
  onToggle: (cardId: string) => void;
}

export const LOTE_INDISPONIVEL = 'A ação em lote está indisponível.';

/** Visível no diálogo, antes do botão de confirmar. */
export const LOTE_EXCLUIR_ALCANCE =
  'Excluir um cartão modelo remove também a cópia de cada aluno derivada dele.';

export function rotuloSelecao(count: number): string {
  if (count === 1) return '1 cartão selecionado';
  return `${count} cartões selecionados`;
}

function ladoAplicado(count: number, aplicado: boolean): string {
  if (count === 1) return aplicado ? '1 foi aplicado' : '1 não foi aplicado';
  return aplicado ? `${count} foram aplicados` : `${count} não foram aplicados`;
}

/** Contagens da própria resposta. `affected.length` não entra: no fio `affected` é número. */
export function relatoDoLote(result: BulkActionResult): LoteRelato {
  const applied = result.affectedCount;
  const skipped = result.notAffected.length;
  const parcial = skipped > 0;
  const motivos = result.notAffected.map((item) => {
    const reason = item.reason.trim();
    return reason.length > 0 ? `${item.cardId}: ${reason}` : item.cardId;
  });
  const message = parcial
    ? `${ladoAplicado(applied, true)} e ${ladoAplicado(skipped, false)}.`
    : applied === 1
      ? '1 cartão aplicado.'
      : `${applied} cartões aplicados.`;
  return { parcial, applied, skipped, message, motivos };
}

/** Ids que continuam selecionados: só os que a rota listou em `notAffected`. */
export function idsQuePermanecemSelecionados(
  selecionados: Iterable<string>,
  result: BulkActionResult,
): string[] {
  const falhos = new Set(result.notAffected.map((item) => item.cardId));
  const restantes: string[] = [];
  Array.from(selecionados).forEach((id) => {
    if (falhos.has(id)) restantes.push(id);
  });
  return restantes;
}

export function statusHttp(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const status = (err as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** 404 ou 405: a rota ainda não está no binário. Não é falha parcial. */
export function rotaDeLoteAusente(err: unknown): boolean {
  const status = statusHttp(err);
  return status === 404 || status === 405;
}
